import {
  ContextualTipId,
  useContextualTip,
  useContextualTipContext,
} from '@resistapp/client/contexts/contextual-tip-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { useEffect, useRef, useState } from 'react';
import { ContextualTip } from './contextual-tip';

interface Props {
  /** Container ref that encapsulates the Pathogen markers bar graph */
  containerRef: React.RefObject<HTMLElement>;
}

export function GeneFocusTip({ containerRef }: Props) {
  const { metricMode, selectedSiteDatum } = useOverviewContext();
  const { setTipEnabled } = useContextualTipContext();
  const { visible, message, dismiss, animationDelay, showDismiss } = useContextualTip(
    ContextualTipId.SITE_FOCUS_GENE_OR_ANTIBIOTIC,
    metricMode,
  );

  // Enable the tip only when a site is selected and the detail boxes are visible
  const siteViewActive = !!selectedSiteDatum;
  useEffect(() => {
    setTipEnabled(ContextualTipId.SITE_FOCUS_GENE_OR_ANTIBIOTIC, siteViewActive);
  }, [siteViewActive, setTipEnabled]);

  // Dynamically locate the first gene label inside the container
  const dynamicAnchorRef = useRef<HTMLElement | null>(null);
  const [, forceUpdate] = useState(0); // used to trigger rerender when anchor found

  useEffect(() => {
    if (!visible) return;
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLElement>('[data-gene-focus-tip-anchor="true"]');
    if (el && dynamicAnchorRef.current !== el) {
      dynamicAnchorRef.current = el;
      forceUpdate(t => t + 1); // ensure tooltip repositions once anchor is found
    }
  }, [visible, containerRef]);

  if (!visible || !dynamicAnchorRef.current) {
    return null;
  }

  return (
    <ContextualTip
      anchorRef={dynamicAnchorRef as unknown as React.RefObject<HTMLElement>}
      message={message || ''}
      onDismiss={dismiss}
      placement="top" // arrow points downwards
      arrowPosition="center" // arrow on the left side of the tooltip
      animationDelay={animationDelay}
      showDismiss={showDismiss}
    />
  );
}
